import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { idFromSlug, cookbookPath } from "@/lib/slug";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, UploadSimple, ArrowsClockwise, Image as ImageIcon, X, Palette, Sparkle } from "@phosphor-icons/react";
import BookCover from "@/components/BookCover";
import SnippetPreview from "@/components/SnippetPreview";

const FONTS = [
  "Nunito", "Manrope", "Playfair Display", "Lora", "Inter",
  "Cormorant Garamond", "Caveat", "Dancing Script",
  // Two extra handwriting-style faces added 2026-02-23 per user request.
  // Kalam is a casual marker-pen hand; Shadows Into Light is a thin notebook hand.
  "Kalam", "Shadows Into Light",
];
const COVER_TITLE_SIZES = [
  { id: "sm",  label: "Small",  className: "scale-90" },
  { id: "md",  label: "Medium", className: "" },
  { id: "lg",  label: "Large",  className: "scale-110" },
  { id: "xl",  label: "X-Large", className: "scale-125" },
];
const PALETTES = [
  { name: "Amber Heirloom", primary: "#FFAE3D", secondary: "#EF8200" },
  { name: "Sage Kitchen", primary: "#8A9A86", secondary: "#5C6F58" },
  { name: "Terracotta", primary: "#D96C4A", secondary: "#A14A2E" },
  { name: "Plum Pantry", primary: "#7A4F6D", secondary: "#4A2F40" },
  { name: "Ocean Blue", primary: "#5A8FBA", secondary: "#2F5A82" },
  { name: "Forest", primary: "#6B8E5A", secondary: "#3F5938" },
  { name: "Charcoal", primary: "#5C5C5C", secondary: "#2C2C2C" },
];

export default function CookbookEdit() {
  const { id: slugId } = useParams();
  const id = idFromSlug(slugId);
  const nav = useNavigate();
  const [cb, setCb] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [seoSuggestBusy, setSeoSuggestBusy] = useState(false);
  const coverRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get(`/cookbooks/${id}`).then((r) => setCb(r.data)).catch(() => toast.error("Cookbook not found"));
  }, [id]);

  if (!cb) return <div className="p-10 text-center">Loading…</div>;

  const set = (k, v) => setCb({ ...cb, [k]: v });

  const uploadImage = async (file, kind) => {
    if (!file) return;
    if (kind === "cover") setUploadingCover(true); else setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "cover") set("cover_image_url", res.url); else set("logo_url", res.url);
      toast.success(`${kind === "cover" ? "Cover" : "Logo"} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { if (kind === "cover") setUploadingCover(false); else setUploadingLogo(false); }
  };

  const save = async () => {
    try {
      await api.patch(`/cookbooks/${id}`, {
        title: cb.title,
        subtitle: cb.subtitle || "",
        show_title_on_cover: cb.show_title_on_cover !== false,
        description: cb.description,
        cover_image_url: cb.cover_image_url,
        logo_url: cb.logo_url,
        font_choice: cb.font_choice,
        cover_title_size: cb.cover_title_size || "md",
        palette_primary: cb.palette_primary,
        palette_secondary: cb.palette_secondary,
        categories: cb.categories,
        privacy: cb.privacy,
        // SEO overrides (Advanced section) — sent as empty strings when
        // cleared so the backend can null them out and fall back to
        // auto-generated meta on the public /cb/{id} page.
        seo_title: cb.seo_title || "",
        seo_description: cb.seo_description || "",
        seo_keywords: cb.seo_keywords || "",
        image_alt: cb.image_alt || "",
      });
      toast.success("Cookbook updated");
      nav(cookbookPath(cb));
    } catch { toast.error("Save failed"); }
  };

  const removeCategory = (c) => set("categories", cb.categories.filter((x) => x !== c));
  const addCategory = () => {
    const c = newCategory.trim();
    if (!c) return;
    if (cb.categories.includes(c)) return;
    set("categories", [...cb.categories, c]);
    setNewCategory("");
  };

  /* Draft optimised SEO fields (title / description / keywords / image_alt)
   * via Claude. Populates the Advanced inputs — user still reviews and
   * saves manually. Non-destructive: only fills fields the LLM returned. */
  const suggestSeo = async () => {
    setSeoSuggestBusy(true);
    try {
      const { data } = await api.post(`/cookbooks/${id}/suggest-seo`);
      if (!data || (!data.seo_title && !data.seo_description && !data.image_alt && !data.seo_keywords)) {
        toast.error("AI didn't return a suggestion — try again in a moment");
        return;
      }
      setCb((prev) => ({
        ...prev,
        seo_title: data.seo_title || prev.seo_title,
        seo_description: data.seo_description || prev.seo_description,
        seo_keywords: data.seo_keywords || prev.seo_keywords,
        image_alt: data.image_alt || prev.image_alt,
      }));
      toast.success("AI suggestions loaded — review and save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally { setSeoSuggestBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <Link to={cookbookPath(cb)} className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block">← Back to cookbook</Link>
      <p className="overline mb-3">Customise</p>
      <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-10 leading-[0.95]">Make it your own.</h1>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left: live preview */}
        <div className="lg:col-span-5">
          <div className="sticky top-24">
            <p className="overline mb-3">Preview</p>
            <BookCover cookbook={cb} size="md" data-testid="cbedit-preview-cover" />
          </div>
        </div>

        {/* Right: editor */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <label className="overline">Title</label>
            <input data-testid="cbedit-title" value={cb.title} onChange={(e) => set("title", e.target.value)} className="input-rf mt-1 !text-2xl !font-display !font-black" />
          </div>

          <div>
            <label className="overline">Cover subtitle (optional)</label>
            <input
              data-testid="cbedit-subtitle"
              value={cb.subtitle || ""}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder="e.g. Family recipes from three generations"
              maxLength={120}
              className="input-rf mt-1 italic"
            />
            <p className="text-xs text-rf-ink-soft mt-1">A short second line printed under the title on the book cover. Leave blank if you don&apos;t want one.</p>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-xl bg-rf-cream-alt">
            <input
              type="checkbox"
              id="cbedit-show-title-on-cover"
              data-testid="cbedit-show-title-on-cover"
              checked={cb.show_title_on_cover !== false}
              onChange={(e) => set("show_title_on_cover", e.target.checked)}
              className="mt-1 w-4 h-4 accent-rf-orange shrink-0"
            />
            <label htmlFor="cbedit-show-title-on-cover" className="text-sm text-rf-ink leading-snug cursor-pointer flex-1">
              <span className="font-bold">Print the title & subtitle on the cover</span>
              <span className="block text-rf-ink-soft text-xs mt-0.5">
                Turn OFF if you&apos;ve uploaded a pre-designed cover image that already has the title baked into it — your image will then show edge-to-edge with no text overlay.
              </span>
            </label>
          </div>

          <div>
            <label className="overline">Description</label>
            <textarea data-testid="cbedit-desc" rows={3} value={cb.description || ""} onChange={(e) => set("description", e.target.value)} className="input-rf mt-1" />
            <p className="text-xs text-rf-ink-soft mt-1">Internal description shown to people who open the cookbook. Doesn&apos;t appear on the cover.</p>
          </div>

          <div>
            <label className="overline">Privacy</label>
            <select data-testid="cbedit-privacy" value={cb.privacy} onChange={(e) => set("privacy", e.target.value)} className="input-rf mt-1">
              <option value="public">Public — list in directory</option>
              <option value="all_pages_shared">All pages shared (private invite)</option>
              <option value="select_pages_shared">Selected pages shared</option>
              <option value="selective_minus_secrets">All except &lsquo;Secret&rsquo; recipes</option>
            </select>
          </div>

          {/* Cover image */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Cover image</label>
              <div className="flex gap-2">
                {cb.cover_image_url && (
                  <button data-testid="cbedit-remove-cover" onClick={() => set("cover_image_url", null)} className="text-xs text-rf-ink-soft font-bold hover:text-red-600"><X size={14} className="inline" /> Remove</button>
                )}
                <button data-testid="cbedit-upload-cover" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="text-xs text-rf-orange font-bold inline-flex items-center gap-1 disabled:opacity-50">
                  <UploadSimple size={14} /> {uploadingCover ? "Uploading…" : (cb.cover_image_url ? "Replace" : "Upload")}
                </button>
                <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "cover")} />
              </div>
            </div>
            {!cb.cover_image_url && <p className="text-xs text-rf-ink-soft">No cover yet — the palette gradient will show instead.</p>}
          </div>

          {/* Logo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Logo (optional)</label>
              <div className="flex gap-2">
                {cb.logo_url && (
                  <button onClick={() => set("logo_url", null)} className="text-xs text-rf-ink-soft font-bold hover:text-red-600"><X size={14} className="inline" /> Remove</button>
                )}
                <button data-testid="cbedit-upload-logo" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="text-xs text-rf-orange font-bold inline-flex items-center gap-1 disabled:opacity-50">
                  <UploadSimple size={14} /> {uploadingLogo ? "Uploading…" : (cb.logo_url ? "Replace" : "Upload")}
                </button>
                <input ref={logoRef} type="file" accept="image/png,image/svg+xml,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "logo")} />
              </div>
            </div>
          </div>

          {/* Palette */}
          <div>
            <label className="overline mb-2 block">Colour palette</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {PALETTES.map((p) => (
                <button
                  key={p.name}
                  data-testid={`cbedit-palette-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => { set("palette_primary", p.primary); set("palette_secondary", p.secondary); }}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${cb.palette_primary === p.primary ? "border-rf-ink" : "border-transparent"}`}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-6 h-6 rounded-full" style={{ background: p.primary }} />
                    <div className="w-6 h-6 rounded-full" style={{ background: p.secondary }} />
                  </div>
                  <p className="text-xs font-bold text-rf-ink">{p.name}</p>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-rf-ink-soft">Primary (hex)</label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={cb.palette_primary} onChange={(e) => set("palette_primary", e.target.value)} className="w-12 h-12 rounded border border-rf-ink/10 cursor-pointer" />
                  <input value={cb.palette_primary} onChange={(e) => set("palette_primary", e.target.value)} className="input-rf font-mono uppercase" />
                </div>
              </div>
              <div>
                <label className="text-xs text-rf-ink-soft">Secondary (hex)</label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={cb.palette_secondary} onChange={(e) => set("palette_secondary", e.target.value)} className="w-12 h-12 rounded border border-rf-ink/10 cursor-pointer" />
                  <input value={cb.palette_secondary} onChange={(e) => set("palette_secondary", e.target.value)} className="input-rf font-mono uppercase" />
                </div>
              </div>
            </div>
          </div>

          {/* Font */}
          <div>
            <label className="overline">Font</label>
            <select data-testid="cbedit-font" value={cb.font_choice} onChange={(e) => set("font_choice", e.target.value)} className="input-rf mt-1">
              {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
            <p className="text-sm mt-2" style={{ fontFamily: cb.font_choice }}>
              The quick brown fox jumps over the lazy dog — 1234567890
            </p>
          </div>

          {/* Cover title size — controls how big the title block renders on
              the front cover. Only affects BookCover.jsx output, not the
              page heading on /cb/{id}. */}
          <div>
            <label className="overline">Cover title size</label>
            <div className="grid grid-cols-4 gap-2 mt-2" data-testid="cbedit-title-size">
              {COVER_TITLE_SIZES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`cbedit-title-size-${s.id}`}
                  onClick={() => set("cover_title_size", s.id)}
                  className={`px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${(cb.cover_title_size || "md") === s.id ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink hover:bg-rf-amber/30"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-rf-ink-soft mt-2">Affects the title lettering on the front cover only.</p>
          </div>

          {/* Tags */}
          <div>
            <label className="overline">Tags</label>
            <div className="flex gap-2 mt-1 mb-3">
              <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())} placeholder="Add a tag" className="input-rf flex-1" />
              <button data-testid="cbedit-add-category" onClick={addCategory} className="btn-outline-ink !py-3 !px-4 !text-sm">+ Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {cb.categories.map((c) => (
                <span key={c} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rf-cream-alt text-rf-ink text-sm font-bold">
                  {c}
                  <button onClick={() => removeCategory(c)} className="text-rf-ink-soft hover:text-red-600"><X size={14} /></button>
                </span>
              ))}
              {!cb.categories.length && <p className="text-sm text-rf-ink-soft">No tags yet.</p>}
            </div>
          </div>

          {/* Advanced — SEO overrides. Collapsible so the normal edit
              flow isn't cluttered. Only surfaced when the cookbook is
              public because private books are noindex anyway. */}
          {cb.privacy === "public" && (
            <details className="border-t border-rf-ink/10 pt-4" data-testid="cbedit-seo-details">
              <summary className="cursor-pointer inline-flex items-center gap-2 font-display font-black text-rf-ink text-lg select-none">
                Advanced · SEO for search engines
              </summary>
              <div className="mt-4 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className="text-xs text-rf-ink-soft italic flex-1 min-w-[240px]">
                    Public cookbooks show up in Google. These fields override the auto-generated title, description and keywords on the <code>/cb/…</code> page. Leave blank to let Recipe Filer fill them in from the cookbook title and description.
                  </p>
                  <button
                    type="button"
                    data-testid="cbedit-seo-draft-ai"
                    onClick={suggestSeo}
                    disabled={seoSuggestBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-rf-ink text-rf-cream hover:bg-rf-ink/90 disabled:opacity-60 disabled:cursor-wait transition-all shrink-0"
                  >
                    <Sparkle size={13} weight="fill" /> {seoSuggestBusy ? "Drafting…" : "Draft with AI"}
                  </button>
                </div>
                <div>
                  <label className="overline">SEO title (≤60 chars)</label>
                  <input
                    data-testid="cbedit-seo-title"
                    value={cb.seo_title || ""}
                    onChange={(e) => set("seo_title", e.target.value.slice(0, 110))}
                    placeholder={cb.title ? `${cb.title} — a family cookbook` : "Cookbook title for Google"}
                    className="input-rf mt-1"
                  />
                  <p className="text-xs text-rf-ink-soft mt-1">{(cb.seo_title || "").length} / 60</p>
                </div>
                <div>
                  <label className="overline">Meta description (145–160 chars)</label>
                  <textarea
                    data-testid="cbedit-seo-description"
                    rows={3}
                    value={cb.seo_description || ""}
                    onChange={(e) => set("seo_description", e.target.value.slice(0, 200))}
                    placeholder="Warm, benefit-led summary — appears in Google search results and social preview cards."
                    className="input-rf mt-1"
                  />
                  <p className="text-xs text-rf-ink-soft mt-1">{(cb.seo_description || "").length} / 160</p>
                </div>
                <div>
                  <label className="overline">Keywords (comma-separated, 10–14 terms)</label>
                  <textarea
                    data-testid="cbedit-seo-keywords"
                    rows={2}
                    value={cb.seo_keywords || ""}
                    onChange={(e) => set("seo_keywords", e.target.value.slice(0, 400))}
                    placeholder="wilson family cookbook, English family recipes, heirloom bakes, Sunday roast recipes, granny's cakes"
                    className="input-rf mt-1"
                  />
                  <p className="text-xs text-rf-ink-soft mt-1">Ignored by Google but read by Bing, DuckDuckGo, Yandex and AI search engines.</p>
                </div>
                <div>
                  <label className="overline">Cover image alt text (≤120 chars)</label>
                  <input
                    data-testid="cbedit-seo-image-alt"
                    value={cb.image_alt || ""}
                    onChange={(e) => set("image_alt", e.target.value.slice(0, 150))}
                    placeholder="A warm illustrated cookbook cover with the title 'The Wilson Family Cookbook' in gold lettering."
                    className="input-rf mt-1"
                  />
                  <p className="text-xs text-rf-ink-soft mt-1">{(cb.image_alt || "").length} / 120 · Helps screen readers and image search.</p>
                </div>
                <SnippetPreview
                  title={cb.seo_title || cb.title || "Cookbook"}
                  description={cb.seo_description || cb.description || ""}
                  url={`https://recipefiler.com/cb/${cb.cookbook_id || id}`}
                  image={cb.cover_image_url || null}
                  brand="Recipe Filer"
                  dataTestidPrefix="snippet-preview-cookbook"
                />
              </div>
            </details>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Link to={cookbookPath(cb)} className="btn-outline-ink">Cancel</Link>
            <button data-testid="cbedit-save" onClick={save} className="btn-amber"><FloppyDisk size={18} /> Save changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}
